import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IPackagingPurchaseOrderDetailedItem,
  IPackagingPurchaseOrderListItem,
} from 'src/types/packaging-purchase-order';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  orders: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingPurchaseOrderListItem[];
  };
  order: IPackagingPurchaseOrderDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  orders: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  order: null,
};

const packagingPurchaseOrderSlice = createSlice({
  name: 'packagingPurchaseOrder',
  initialState,
  reducers: {
    removePackagingPurchaseOrder: (state, action) => {
      const { orderId } = action.payload;

      const index = state.orders.items.findIndex((order) => order._id === orderId);

      if (index > -1) {
        action.payload.index = index;
        state.orders.items = state.orders.items.filter((order) => order._id !== orderId);
      }
    },
    rollbackPackagingPurchaseOrderDeletion: (state, action) => {
      const { order, index } = action.payload;

      state.orders.items.splice(index, 0, order);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagingPurchaseOrders.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingPurchaseOrders.fulfilled, (state, action) => {
        state.status = 'success';
        state.orders = action.payload;
      })
      .addCase(getPackagingPurchaseOrders.rejected, (state, action) => {
        state.orders = initialState.orders;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingPurchaseOrderById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingPurchaseOrderById.fulfilled, (state, action) => {
        state.status = 'success';
        state.order = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingPurchaseOrderById.rejected, (state, action) => {
        state.order = initialState.order;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default packagingPurchaseOrderSlice.reducer;

export const { removePackagingPurchaseOrder, rollbackPackagingPurchaseOrderDeletion } =
  packagingPurchaseOrderSlice.actions;

export const getPackagingPurchaseOrders = createAsyncThunk(
  'packagingPurchaseOrder/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    supplier?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, supplier, startDate, endDate, sortBy, sort } = params;

    try {
      const response = await axios.get(
        `packaging/purchase-order?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&supplier=${supplier ?? ''}&startDate=${startDate ? startDate.toISOString() : ''}&endDate=${
          endDate ? endDate.toISOString() : ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingPurchaseOrderById = createAsyncThunk(
  'packagingPurchaseOrder/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/purchase-order/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deletePackagingPurchaseOrder = createAsyncThunk(
  'packagingPurchaseOrder/delete',
  async (params: { order: IPackagingPurchaseOrderListItem; index: number }, { dispatch }) => {
    const { order, index } = params;

    try {
      const response = await axios.delete(`packaging/purchase-order/${order._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingPurchaseOrderDeletion({ order, index }));
      throw err;
    }
  }
);

export interface TCreatePackagingPurchaseOrderItem {
  packagingItem: string;
  quantity: number;
  unitPrice?: number | null;
  discount: number;
  totalAfterDiscount: number;
}

export interface TCreatePackagingPurchaseOrder {
  supplier: string;
  purchaseDate: Date;
  discount: number;
  isPercentageDiscount: boolean;
  totalAfterDiscount: number;
  remarks: string | null;
  items: TCreatePackagingPurchaseOrderItem[];
}

export const createPackagingPurchaseOrder = createAsyncThunk(
  'packagingPurchaseOrder/create',
  async (params: { poTransaction: TCreatePackagingPurchaseOrder }) => {
    const { poTransaction } = params;
    const response = await axios.post('packaging/purchase-order', poTransaction);
    return response.data.data;
  }
);

export const updatePackagingPurchaseOrder = createAsyncThunk(
  'packagingPurchaseOrder/update',
  async (params: {
    purchaseOrderId: string;
    poTransaction: TCreatePackagingPurchaseOrder;
  }) => {
    const { poTransaction, purchaseOrderId } = params;
    const response = await axios.put(
      `packaging/purchase-order/${purchaseOrderId}`,
      poTransaction
    );
    return response.data.data;
  }
);
